import { useState, useRef, useMemo, useEffect } from 'react';
import { MapPin, Video, Loader2, Play, Trash2, Globe } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { GlassCard, API } from './GlassCard';
import useAutoSave from '@/hooks/useAutoSave';
import AutoSaveIndicator from '@/components/AutoSaveIndicator';

/**
 * Vendor-only: edit the public service area (city/state + radius) and
 * upload/replace the 60-second intro video displayed at the top of the
 * profile and of every service page.
 *
 * Renders two GlassCards side-by-side on wide screens, stacked on mobile.
 */
export default function ServiceAreaAndVideo({ vendor, getAuthHeaders, onChanged }) {
  const [city, setCity] = useState(vendor?.service_city || '');
  const [state, setState] = useState(vendor?.service_state || '');
  const [country, setCountry] = useState(vendor?.service_country || 'US');
  const [radius, setRadius] = useState(vendor?.service_radius_miles || 25);
  const [savingArea, setSavingArea] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [introVideoUrl, setIntroVideoUrl] = useState(vendor?.intro_video_url || '');
  const fileRef = useRef(null);

  // Sync intro video URL when the vendor prop arrives/refreshes after the
  // initial render. Without this the video silently "disappears" on tab
  // re-mount or after a parent refetch, because the state initializer only
  // runs once with the first (possibly null) vendor prop.
  useEffect(() => {
    setIntroVideoUrl(vendor?.intro_video_url || '');
  }, [vendor?.intro_video_url]);

  // Auto-save service area: only fire after the form has fully hydrated from
  // props, and only when both city & state are filled. Reuses the existing
  // POST /vendors/me/service-area endpoint so the geocoder still runs.
  const loadedOnce = useRef(false);
  useEffect(() => { loadedOnce.current = true; }, []);

  const areaPayload = useMemo(() => ({
    city: city.trim(),
    state: state.trim(),
    country: country || 'US',
    radius_miles: Number(radius) || 25,
  }), [city, state, country, radius]);

  const areaSave = useAutoSave(
    areaPayload,
    async (val) => {
      if (!loadedOnce.current) return;
      if (!val.city || !val.state) return; // backend requires both
      const r = await fetch(`${API}/vendors/me/service-area`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(val),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || 'Failed to save');
      onChanged?.();
    },
    { delay: 1500, skipFirst: true },
  );

  const saveArea = async () => {
    if (!city.trim() || !state.trim()) {
      toast.error('City and state are required');
      return;
    }
    setSavingArea(true);
    try {
      const r = await fetch(`${API}/vendors/me/service-area`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ city: city.trim(), state: state.trim(), country: country || 'US', radius_miles: Number(radius) || 25 }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Failed to save');
      toast.success('Service area saved');
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingArea(false);
    }
  };

  const handleVideoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) { toast.error('Must be a video file'); return; }
    if (f.size > 200 * 1024 * 1024) { toast.error('Max 200 MB — aim for a 60s clip (4K OK)'); return; }
    setUploadingVideo(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const r = await fetch(`${API}/vendors/me/intro-video`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
        body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Upload failed');
      setIntroVideoUrl(data.url);
      toast.success('Intro video uploaded');
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploadingVideo(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeVideo = async () => {
    if (!introVideoUrl) return;
    if (!window.confirm('Remove intro video?')) return;
    try {
      const r = await fetch(`${API}/vendors/me/intro-video`, { method: 'DELETE', headers: getAuthHeaders() });
      if (r.ok) {
        setIntroVideoUrl('');
        toast.success('Video removed');
        onChanged?.();
      }
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Service area */}
      <GlassCard className="p-6 space-y-4" data-testid="service-area-card">
        <div className="flex items-center gap-2">
          <MapPin className="text-[#A78BFA]" size={18} />
          <h2 className="text-lg font-semibold text-white font-syne">Service Area</h2>
          <div className="ml-auto">
            <AutoSaveIndicator status={areaSave.status} lastSaved={areaSave.lastSaved} />
          </div>
        </div>
        <p className="text-[#9CA3AF] text-xs">
          Shown on the public map and your profile. Exact address is never published — only your city-level location.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[#9CA3AF] text-xs">City *</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Tulsa" className="input-field bg-white/5 mt-1" data-testid="area-city" />
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">State / Province *</Label>
            <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="OK" className="input-field bg-white/5 mt-1" data-testid="area-state" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[#9CA3AF] text-xs">Country</Label>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="input-field bg-white/5 mt-1 w-full rounded-xl border border-white/10 p-2 text-white text-sm" data-testid="area-country">
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="MX">Mexico</option>
            </select>
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs flex items-center gap-1">
              <Globe size={12} /> Radius (miles)
            </Label>
            <Input type="number" min={1} max={500} value={radius} onChange={(e) => setRadius(e.target.value)} className="input-field bg-white/5 mt-1" data-testid="area-radius" />
          </div>
        </div>
        {vendor?.service_lat && (
          <p className="text-[10px] text-green-400/80">
            ✓ Geocoded to {vendor.service_display_name || `${vendor.service_city}, ${vendor.service_state}`}
          </p>
        )}
        <button
          onClick={saveArea}
          disabled={savingArea}
          className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] disabled:opacity-50"
          data-testid="save-service-area"
        >
          {savingArea ? 'Saving…' : 'Save Service Area'}
        </button>
      </GlassCard>

      {/* Intro video */}
      <GlassCard className="p-6 space-y-4" data-testid="intro-video-card">
        <div className="flex items-center gap-2">
          <Video className="text-[#A78BFA]" size={18} />
          <h2 className="text-lg font-semibold text-white font-syne">Intro Video</h2>
        </div>
        <p className="text-[#9CA3AF] text-xs">
          Show customers who you are. Appears at the top of your profile and every service page. 60 sec max · 200 MB max (4K friendly).
        </p>

        {introVideoUrl ? (
          <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black aspect-video" data-testid="intro-video-preview">
            <video src={`${process.env.REACT_APP_BACKEND_URL}${introVideoUrl}`} controls playsInline className="w-full h-full object-contain" />
            <button
              onClick={removeVideo}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/80 hover:bg-red-500 text-white"
              title="Remove"
              data-testid="remove-intro-video"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ) : (
          <div className="p-8 rounded-xl border-2 border-dashed border-white/10 bg-white/[0.02] text-center">
            <Play className="w-10 h-10 mx-auto text-[#64748B] mb-2" />
            <p className="text-[#9CA3AF] text-sm">No intro video yet</p>
          </div>
        )}

        <input ref={fileRef} type="file" accept="video/*" onChange={handleVideoFile} className="hidden" data-testid="intro-video-file" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploadingVideo}
          className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="upload-intro-video-btn"
        >
          {uploadingVideo ? <><Loader2 className="animate-spin" size={14} /> Uploading…</> : (introVideoUrl ? 'Replace Video' : 'Upload Video')}
        </button>
      </GlassCard>
    </div>
  );
}
